import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { MAP_ELEMENT } from "../../../contexts/WorldMapContext/constants";
import { SiteMarker } from "../../../contexts/WorldMapContext/Marker";
import { selectOneSetting, selectViewMode } from "../../../util/store/selectors";
import { selectSite } from "../../../util/store/site/constants";
import { ISite } from "../../../util/varibles/interface";
import { VIEW_MODE } from '../../../util/varibles/permission';
import { DashboardSetting } from "../../../util/varibles/userSetting";
import { dashboardActions } from "../reducer";

interface IProps {
    ids: Set<string>
}

const Sites: React.FC<IProps> = React.memo(({ ids }) => {
    const sites = useSelector(selectSite.dashboard);
    const viewMode = useSelector(selectViewMode);
    const areaIds = useSelector(selectOneSetting(DashboardSetting.PRODUCT_AREAS));
    const isActiveSite = useSelector(selectOneSetting(DashboardSetting.ACTIVE_SITE));
    const dispatch = useDispatch();

    const areaIdSet = useMemo(() => new Set(areaIds), [areaIds]);
    const sitesWithEmptyState = useMemo(() => sites.map(site => ({
        ...site,
        is_empty: !site.units?.some(unit => (unit.fish_amount ?? 0) > 0),
    })), [sites]);

    const show = useMemo(() => {
        return (site: ISite) => {
            if (ids.size > 0 && !ids.has(site.id)) {
                return false;
            }

            const isInArea =
                areaIdSet.size === 0 ||
                areaIdSet.has(site.prod_area);

            if (!isInArea) {
                return false;
            }

            if (viewMode === VIEW_MODE.PLANNER && isActiveSite) {
                return !site.is_empty
            }

            return (
                viewMode === VIEW_MODE.PLANNER ||
                viewMode === VIEW_MODE.SITE
            );
        };
    }, [areaIdSet, ids, isActiveSite, viewMode]);

    const onClick = useCallback((data: ISite) => {
        dispatch(dashboardActions.showInfo({ data, targetType: MAP_ELEMENT.SITE }));
    }, [dispatch])

    const visibleSites = useMemo(
        () => sitesWithEmptyState.filter(show),
        [show, sitesWithEmptyState]
    );

    return <>
        {visibleSites.map(site => {
            const { id } = site;
            return <SiteMarker
                key={id}
                data={site}
                onClick={() => onClick(site)}
            />
        })}
    </>;
});

export default Sites;
